import React, { createContext, useContext, useState, useCallback } from "react";
import { authApi } from "../services/api";

const AuthContext = createContext(null);

function loadUser() {
  try {
    const stored = localStorage.getItem("lp_user");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(loadUser);
  const [loading, setLoading] = useState(false);

  const saveSession = useCallback((userData, accessToken, refreshToken) => {
    localStorage.setItem("lp_user", JSON.stringify(userData));
    localStorage.setItem("lp_access_token", accessToken);
    localStorage.setItem("lp_refresh_token", refreshToken);
    setUser(userData);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem("lp_user");
    localStorage.removeItem("lp_access_token");
    localStorage.removeItem("lp_refresh_token");
    setUser(null);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await authApi.login({ email, password });
      saveSession(data.user, data.accessToken, data.refreshToken);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [saveSession]);

  const signup = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await authApi.signup(payload);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch (_) {}
    clearSession();
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authApi.getMe();
      const updated = data.user;
      localStorage.setItem("lp_user", JSON.stringify(updated));
      setUser(updated);
      return updated;
    } catch {
      clearSession();
      return null;
    }
  }, [clearSession]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAdmin: user?.role === "admin",
      isAuthenticated: !!user,
      login,
      signup,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
